'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo } from 'react';
import { Card, CardBody, CardHeader, Button, Chip, Image, Skeleton, ScrollShadow, Divider, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@heroui/react';
import { Icon } from '@iconify/react';
import { motion } from 'motion/react';
import { formatUnits, type Address } from 'viem';
import { useChainId, useConnection } from 'wagmi';
import { writeContract } from '@wagmi/core';
import { config } from '@/config/index';
import { useNFTOffers } from '@/hooks/marketplace/useNFTOffers';
import { getTokensByChainId } from '@/constants/marketplace/tokens';
import { getOptimizedImageUrl } from '@/util/ipfs';
import { useNFTViewer } from '@/hooks/useNFTViewer';
import { getMarketplaceContract } from '@/constants/marketplace/contracts';
import { marketplaceV3Abi } from '@/constants/abi/marketplaceV3Abi';
import toast from '@/util/toast';
interface CollectionOffersViewProps {
  contractAddress: string;
  collectionName: string;
  nftsWithOffers: Array<{
    tokenId: string;
    metadata?: {
      name?: string;
      image?: string;
    };
  }>;
  onBack: () => void;
}
interface NFTOfferCardProps {
  contractAddress: string;
  tokenId: string;
  metadata?: {
    name?: string;
    image?: string;
  };
  collectionName: string;
}

// Utility functions for localStorage
const getDeclinedOffers = (): Set<string> => {
  if (typeof window === 'undefined') return new Set();
  try {
    const declined = localStorage.getItem('declinedOffers');
    return declined ? new Set(JSON.parse(declined)) : new Set();
  } catch (error) {
    console.error('Error reading declined offers from localStorage:', error);
    return new Set();
  }
};
const addDeclinedOffer = (offerId: string): void => {
  if (typeof window === 'undefined') return;
  try {
    const declined = getDeclinedOffers();
    declined.add(offerId);
    localStorage.setItem('declinedOffers', JSON.stringify(Array.from(declined)));
  } catch (error) {
    console.error('Error saving declined offer to localStorage:', error);
  }
};
const generateOfferId = (offer: any): string => {
  return `${offer.offeror}-${offer.assetContract}-${offer.tokenId}-${offer.price}-${offer.expirationTimestamp}`;
};
const NFTOfferCard: React.FC<NFTOfferCardProps> = ({
  contractAddress,
  tokenId,
  metadata,
  collectionName
}) => {
  const chainId = useChainId();
  const {
    address: userAddress
  } = useConnection();
  const {
    activeOffers,
    highestOffer,
    loading,
    refetch: refetchOffers
  } = useNFTOffers(contractAddress, tokenId);
  const supportedTokens = getTokensByChainId(chainId);
  const {
    openNFTModal
  } = useNFTViewer();
  const {
    isOpen,
    onOpen,
    onClose
  } = useDisclosure();
  const [processingOfferId, setProcessingOfferId] = useState<string | null>(null);
  const [declinedOffers, setDeclinedOffers] = useState<Set<string>>(getDeclinedOffers());
  const marketplaceContract = getMarketplaceContract(chainId);

  // Filter out declined offers
  const visibleOffers = useMemo(() => {
    return activeOffers.filter(offer => {
      const offerId = generateOfferId(offer);
      return !declinedOffers.has(offerId);
    });
  }, [activeOffers, declinedOffers]);
  const visibleHighestOffer = useMemo(() => {
    if (visibleOffers.length === 0) return null;
    return visibleOffers.reduce((highest, current) => current.price > highest.price ? current : highest);
  }, [visibleOffers]);
  const optimizedImageUrl = useMemo(() => {
    if (!metadata?.image) return null;
    return getOptimizedImageUrl(metadata.image.trim(), 200, 200, 88);
  }, [metadata?.image]);
  const getTokenInfo = (currencyAddress: Address) => {
    return supportedTokens?.tokens.find(token => token.address.toLowerCase() === currencyAddress.toLowerCase());
  };
  const formatOfferPrice = (price: bigint, currency: Address) => {
    const tokenInfo = getTokenInfo(currency);
    const decimals = tokenInfo?.decimals || 18;
    const symbol = tokenInfo?.symbol || 'TOKEN';
    const amount = formatUnits(price, decimals);
    const numAmount = parseFloat(amount);
    if (numAmount < 1) {
      return `${numAmount.toFixed(4)} ${symbol}`;
    } else if (numAmount < 1000) {
      return `${numAmount.toFixed(2)} ${symbol}`;
    } else {
      return `${numAmount.toLocaleString(undefined, {
        maximumFractionDigits: 0
      })} ${symbol}`;
    }
  };
  const handleViewNFT = () => {
    openNFTModal({
      chainId: chainId!,
      contractAddress: contractAddress as Address,
      tokenId: BigInt(tokenId),
      initialData: metadata ? {
        metadata,
        owner: null,
        isERC1155: false,
        balance: undefined
      } : undefined
    });
  };
  const handleAcceptOffer = async (offer_0: any) => {
    if (!marketplaceContract?.address || !userAddress) {
      toast.error('Marketplace contract not found or wallet not connected');
      return;
    }
    const offerId_0 = generateOfferId(offer_0);
    setProcessingOfferId(offerId_0);
    try {
      console.log('Accepting offer:', offer_0);

      // Call the marketplace contract to accept the offer
      const hash = await writeContract(config, {
        address: marketplaceContract.address as Address,
        abi: marketplaceV3Abi,
        functionName: 'acceptOffer',
        args: [offer_0.offerId],
        // Assuming the offer has an offerId field
        chainId
      });
      toast.success('Offer accepted successfully!');
      console.log('Transaction hash:', hash);

      // Refresh offers after successful acceptance
      setTimeout(() => {
        refetchOffers();
      }, 2000);
    } catch (error: any) {
      console.error('Error accepting offer:', error);
      toast.error(`Failed to accept offer: ${error.message || 'Unknown error'}`);
    } finally {
      setProcessingOfferId(null);
    }
  };
  const handleDeclineOffer = async (offer_1: any) => {
    const offerId_1 = generateOfferId(offer_1);
    setProcessingOfferId(offerId_1);
    try {
      // Add to declined offers in localStorage
      addDeclinedOffer(offerId_1);

      // Update local state to hide the offer immediately
      setDeclinedOffers(prev => new Set([...prev, offerId_1]));
      toast.success('Offer declined and hidden');
    } catch (error_0) {
      console.error('Error declining offer:', error_0);
      toast.error('Failed to decline offer');
    } finally {
      setProcessingOfferId(null);
    }
  };
  if (loading) {
    return <Card className="w-full">
        <CardBody className="p-4">
          <div className="flex gap-4">
            <Skeleton className="w-16 h-16 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-6 w-1/3" />
            </div>
          </div>
        </CardBody>
      </Card>;
  }
  if (visibleOffers.length === 0) {
    return null;
  }
  return <>
      <Card className="w-full hover:shadow-md transition-shadow cursor-pointer" isPressable onPress={onOpen}>
        <CardBody className="p-4">
          <div className="flex gap-4 items-center">
            {/* NFT Image */}
            <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-default-100 flex-shrink-0">
              {optimizedImageUrl ? <Image src={optimizedImageUrl} alt={metadata?.name || `Token #${tokenId}`} className="w-full h-full object-cover" classNames={{
              wrapper: "w-full h-full",
              img: "w-full h-full object-cover"
            }} /> : <div className="w-full h-full flex items-center justify-center">
                  <Icon icon="mdi:image-off" className="text-2xl text-default-400" />
                </div>}
            </div>

            {/* NFT Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold truncate text-sm">
                {metadata?.name || `Token #${tokenId}`}
              </h3>
              <p className="text-xs text-default-500 mb-2">
                #{tokenId}
              </p>

              {/* Offer Summary */}
              <div className="flex items-center gap-2">
                <Chip size="sm" color="success" variant="flat">
                  {visibleOffers.length} offer{visibleOffers.length !== 1 ? 's' : ''}
                </Chip>
                {visibleHighestOffer && <span className="text-xs font-medium text-success-700">
                    Best: {formatOfferPrice(visibleHighestOffer.price, visibleHighestOffer.currency)}
                  </span>}
              </div>
            </div>

            {/* Action Button */}
            <div className="flex flex-col gap-2">
              <Button size="sm" color="primary" variant="flat" onPress={handleViewNFT} startContent={<Icon icon="mdi:eye" />}>
                View NFT
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Offers Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader>
            <div className="flex items-center gap-3">
              {optimizedImageUrl && <div className="w-12 h-12 rounded-lg overflow-hidden bg-default-100 relative">
                  <Image src={optimizedImageUrl} alt={metadata?.name || `Token #${tokenId}`} className="w-full h-full object-cover" />
                </div>}
              <div>
                <h3 className="font-semibold">
                  {metadata?.name || `Token #${tokenId}`}
                </h3>
                <p className="text-sm text-default-500">
                  {collectionName} • {visibleOffers.length} offer{visibleOffers.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              {visibleOffers.map((offer_2, index) => {
              const offerId_2 = generateOfferId(offer_2);
              const isProcessing = processingOfferId === offerId_2;
              return <Card key={index} className="border">
                    <CardBody className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-lg">
                              {formatOfferPrice(offer_2.price, offer_2.currency)}
                            </span>
                            {offer_2 === visibleHighestOffer && <Chip size="sm" color="success" variant="flat">
                                Highest
                              </Chip>}
                          </div>
                          <div className="text-sm text-default-600">
                            <p>Offeror: <span className="font-mono">{`${offer_2.offeror.slice(0, 6)}...${offer_2.offeror.slice(-4)}`}</span></p>
                            <p>Quantity: {offer_2.quantity.toString()}</p>
                            <p>Expires: {new Date(Number(offer_2.expirationTimestamp) * 1000).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" color="success" variant="flat" onPress={() => handleAcceptOffer(offer_2)} isLoading={isProcessing} isDisabled={isProcessing}>
                            Accept
                          </Button>
                          <Button size="sm" color="danger" variant="flat" onPress={() => handleDeclineOffer(offer_2)} isLoading={isProcessing} isDisabled={isProcessing}>
                            Decline
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                  </Card>;
            })}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button onPress={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>;
};
export const CollectionOffersView: React.FC<CollectionOffersViewProps> = t0 => {
  const $ = _c(17);
  const {
    contractAddress,
    collectionName,
    nftsWithOffers,
    onBack
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      x: 20
    };
    t2 = {
      opacity: 1,
      x: 0
    };
    t3 = {
      opacity: 0,
      x: -20
    };
    t4 = {
      type: "spring",
      stiffness: 300,
      damping: 30
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div><h2 className="font-semibold text-sm md:text-md">Offers to buy your NFTs in this collection:</h2></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon icon="mdi:close" className="text-lg" />;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== onBack) {
    t7 = <div className="flex items-center justify-between px-2 pb-2 -mt-4 pt-2 bg-slate-700/50">{t5}<Button size="sm" variant="light" color="default" isIconOnly={true} onPress={onBack}>{t6}</Button></div>;
    $[6] = onBack;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== collectionName || $[9] !== contractAddress || $[10] !== nftsWithOffers) {
    t8 = nftsWithOffers.length === 0 ? <div className="text-center py-12"><Icon icon="solar:hand-money-bold" className="text-6xl text-default-300 mb-4" /><h3 className="text-lg font-medium text-default-600 mb-2">No Active Offers</h3><p className="text-default-500">This collection doesn't have any active offers at the moment.</p></div> : nftsWithOffers.map(nft => <NFTOfferCard key={nft.tokenId} contractAddress={contractAddress} tokenId={nft.tokenId} metadata={nft.metadata} collectionName={collectionName} />);
    $[8] = collectionName;
    $[9] = contractAddress;
    $[10] = nftsWithOffers;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t8) {
    t9 = <div className="flex-1 overflow-hidden"><ScrollShadow className="h-full"><div className="p-4 space-y-3">{t8}</div></ScrollShadow></div>;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t9) {
    t10 = <motion.div initial={t1} animate={t2} exit={t3} transition={t4} className="h-full flex flex-col">{t7}{t9}</motion.div>;
    $[14] = t7;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
};